import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { Search } from '@mui/icons-material'
import {
  ChartWithNoteWrapper,
  CustomTableCell,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  Ordinator,
  OrdinatorDrawer,
  TableHeadWithoutOrdenation,
  AutoCompleteWrapper
} from '../../components'
import useReportOptimizeCounterMeasure from '../../hooks/useReportOptimizeCounterMeasure'
import HourFilter from '../../components/HourFilter'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
  background-color: #f1f3f5;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`

const SearchContainer = styled.div`
  display: flex;
  flex: 2;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid #d6d7db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 40px;
  gap: 8px;
  min-width: 250px;
`

const StyledInput = styled.input`
  border: 0;
  width: 100%;
  outline: none;
`

const StickyTableCell = styled(TableCell)`
  min-width: 50px;
  left: 0;
  position: sticky;
  z-index: 2;
  background-color: #fff;
`

const now = ({ extraHour }) => {
  let dateNow = Date.now()

  if (extraHour) {
    dateNow = dateNow + 1000 * 60 * 60 * extraHour
  }

  let date = new Date(dateNow).getHours()

  if (date < 10) {
    return `0${date}`
  }

  return date
}

const rowsPerPage = 10

const ReportOptimizeCounterMeasure = () => {
  const { t } = useTranslation('common')
  const baseHeadState = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('colaborator'),
      align: 'left',
      sticky: true,
    },
  ]

  const headState = [
    ...baseHeadState,
    {
      id: 'fatigueLevel8h0',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: null })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h1',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 1 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h2',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 2 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h3',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 3 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h4',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 4 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h5',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 5 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h6',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 6 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h7',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 7 })}h`,
      align: 'center',
    },
    {
      id: 'whiteSpace',
      numeric: false,
      disablePadding: false,
      label: '',
      align: 'center',
      notSort: true,
    },
    {
      id: 'fatigueLevel8h8',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: null })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h9',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 1 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h10',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 2 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h11',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 3 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h12',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 4 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h13',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 5 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h14',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 6 })}h`,
      align: 'center',
    },
    {
      id: 'fatigueLevel8h15',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 7 })}h`,
      align: 'center',
    },
  ]
  const [header, setHeader] = useState(headState)
  const [page, setPage] = useState(0)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const [openOrderDrawer, setOpenOrderDrawer] = useState(false)
  const {
    loading: loadingData,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    onInputValueChange,
    handleType,
    setSelectedHourRange,
    isHourRangeSelected,
    selectedHourRange,
    selectedFilterOptions,
    onSelectOrderSave,
    graphMessage,
    onSelectFilter,
    onSelectUser,
    selectedUser,
    setSelectedUser,
  } = useReportOptimizeCounterMeasure()
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  const getStyleBasedViewportWidth = () => {
    if (width <= 1290) {
      return {
        padding: 0,
      }
    }

    return {}
  }

  useEffect(() => {
    if (selectedHourRange) {
      const hoursToBeDisplayed = selectedHourRange / 8

      let headStateCopy = headState
      headStateCopy[1].label = `${now({ extraHour: null })}h`
      headStateCopy[2].label = `${now({ extraHour: hoursToBeDisplayed })}h`
      headStateCopy[3].label = `${now({ extraHour: hoursToBeDisplayed * 2 })}h`
      headStateCopy[4].label = `${now({ extraHour: hoursToBeDisplayed * 3 })}h`
      headStateCopy[5].label = `${now({ extraHour: hoursToBeDisplayed * 4 })}h`
      headStateCopy[6].label = `${now({ extraHour: hoursToBeDisplayed * 5 })}h`
      headStateCopy[7].label = `${now({ extraHour: hoursToBeDisplayed * 6 })}h`
      headStateCopy[8].label = `${now({ extraHour: hoursToBeDisplayed * 7 })}h`

      headStateCopy[10].label = `${now({ extraHour: null })}h`
      headStateCopy[11].label = `${now({ extraHour: hoursToBeDisplayed })}h`
      headStateCopy[12].label = `${now({ extraHour: hoursToBeDisplayed * 2 })}h`
      headStateCopy[13].label = `${now({ extraHour: hoursToBeDisplayed * 3 })}h`
      headStateCopy[14].label = `${now({ extraHour: hoursToBeDisplayed * 4 })}h`
      headStateCopy[15].label = `${now({ extraHour: hoursToBeDisplayed * 5 })}h`
      headStateCopy[16].label = `${now({ extraHour: hoursToBeDisplayed * 6 })}h`
      headStateCopy[17].label = `${now({ extraHour: hoursToBeDisplayed * 7 })}h`

      setHeader(headStateCopy)
    }
  }, [selectedHourRange])

  useEffect(() => {
    setPage(0)
  }, [selectedFilterOptions])

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () => [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows],
  )

  const handleHourRange = value => {
    if (!loadingData) setSelectedHourRange(value)
  }

  const renderCustomTableCells = ({ row, initialIndex, finalIndex }) => {
    let elements = []

    for (let index = initialIndex; index <= finalIndex; index++) {
      const fatigueString = `fatigueLevel8h${index}`

      elements.push({
        column: (
          <CustomTableCell
            type='value'
            data={row[fatigueString].fatigueLevel}
            align='center'
            sxx={getStyleBasedViewportWidth()}
            isSleeping={row[fatigueString].isSleeping}
            isWorking={row[fatigueString].isWorking}
          />
        ),
      })
    }

    return elements
  }

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 20, fontWeight: 600 }}>
            {t('reports.optimize_counter.title')}

            </span>
            <Tooltip
              title={
                <div style={{ maxWidth: 300 }}>
                  <p>
                    <strong>
                      {t('reports.optimize_counter.tooltip.one')}
                    </strong>
                  </p>
                  <p>
                    <strong>{t('reports.optimize_counter.tooltip.two.strong')}</strong> {t('reports.optimize_counter.tooltip.two.text')}
                  </p>
                  <br />
                  <p>
                    <strong>{t('reports.optimize_counter.tooltip.three.strong')}</strong> {t('reports.optimize_counter.tooltip.three.strong')}.
                  </p>
                  <br />
                  <p>
                    {t('reports.optimize_counter.tooltip.four')}
                  </p>
                </div>
              }
              placement='right'
              arrow
            >
              <HelpOutline fontSize='24' />
            </Tooltip>
          </div>
        </Header>
        <BodyContainer>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1.5 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <HourFilter
                  onClick={() => handleHourRange(8)}
                  text='8h'
                  isSelected={isHourRangeSelected({ hourRange: 8 })}
                />
                <HourFilter
                  onClick={() => handleHourRange(16)}
                  text='16h'
                  isSelected={isHourRangeSelected({ hourRange: 16 })}
                />
                <HourFilter
                  onClick={() => handleHourRange(24)}
                  text='24h'
                  isSelected={isHourRangeSelected({ hourRange: 24 })}
                />
                <HourFilter
                  onClick={() => handleHourRange(48)}
                  text='48h'
                  isSelected={isHourRangeSelected({ hourRange: 48 })}
                />
                <HourFilter
                  onClick={() => handleHourRange(72)}
                  text='72h'
                  isSelected={isHourRangeSelected({ hourRange: 72 })}
                />
                <HourFilter
                  onClick={() => handleHourRange(96)}
                  text='96h'
                  isSelected={isHourRangeSelected({ hourRange: 96 })}
                />
                <Filter onClick={() => setOpenFilterDrawer(true)} />
                <Ordinator onClick={() => setOpenOrderDrawer(prev => !prev)} />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            }).length > 0 && (
              <GroupedFilterTags
                tags={selectedFilterOptionsList({
                  selectedFilterOptions,
                })}
                onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                maxTagsToShow={getMaxTagsToShow({ width })}
              />
            )}
          </div>

          <ChartWithNoteWrapper
            customMessage={
              <>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <span>
                    {t('reports.optimize_counter.custom_message.avg_pred')} <b>{graphMessage?.totalPrediction}</b>
                  </span>
                  <span>
                    {t('reports.optimize_counter.custom_message.avg_opt')} <b>{graphMessage?.totalOptimization}</b>
                  </span>
                </div>

                <div style={{ marginTop: '1em' }}>
                  {t('reports.optimize_counter.custom_message.calculation')} <b>{graphMessage?.totalUsers} {t('reports.optimize_counter.custom_message.users')}</b>{' '}
                  {t('reports.optimize_counter.custom_message.totaling')} <b>{graphMessage?.totalHours}</b> {t('reports.optimize_counter.custom_message.hours_eval')}
                </div>
              </>
            }
          >
            <TableContainer>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby='reportOptimizeCounterMeasureTable'
              >
                <TableHeadWithoutOrdenation
                  headCells={header}
                  sxx={getStyleBasedViewportWidth()}
                  groupingTable={
                    <>
                      <TableRow>
                        <TableCell
                          sx={{ borderBottom: 0 }}
                          colSpan={1}
                        ></TableCell>
                        <TableCell
                          align='center'
                          colSpan={8}
                          sx={{ fontSize: '1.2rem' }}
                        >
                          {t('reports.optimize_counter.header.prediction')}
                        </TableCell>
                        <TableCell
                          sx={{ borderBottom: 0 }}
                          colSpan={1}
                        ></TableCell>
                        <TableCell
                          align='center'
                          colSpan={8}
                          sx={{ fontSize: '1.2rem' }}
                        >
                          {t('reports.optimize_counter.header.optimization')}
                        </TableCell>
                      </TableRow>
                    </>
                  }
                />
                <TableBody>
                  {!loadingData ? (
                    <>
                      {rows.length > 0 ? (
                        <>
                          {visibleRows.map((row, index) => {
                            const labelId = `enhanced-table-checkbox-${index}`

                            return (
                              <TableRow
                                hover
                                tabIndex={-1}
                                key={row.id}
                              >
                                <StickyTableCell
                                  component='td'
                                  id={labelId}
                                  scope='row'
                                  padding='none'
                                  width={120}
                                >
                                  {row.employee}
                                </StickyTableCell>
                                {renderCustomTableCells({
                                  row,
                                  initialIndex: 0,
                                  finalIndex: 7,
                                }).map(({ column }) => column)}
                                <CustomTableCell
                                  type='blank'
                                  align='center'
                                />
                                {renderCustomTableCells({
                                  row,
                                  initialIndex: 8,
                                  finalIndex: 15,
                                }).map(({ column }) => column)}
                              </TableRow>
                            )
                          })}
                          {emptyRows() > 0 && (
                            <TableRow
                              style={{
                                height: 53 * emptyRows(),
                              }}
                            >
                              <TableCell colSpan={18} />
                            </TableRow>
                          )}
                        </>
                      ) : (
                        <TableRow style={{ height: 53 * 10 }}>
                          <TableCell
                            colSpan={18}
                            align='center'
                          >
                            {t('no_data')}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ) : (
                    <TableRow style={{ height: 53 * 10 }}>
                      <TableCell
                        colSpan={18}
                        align='center'
                      >
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={rows.length}
              showFirstButton
              showLastButton
              labelRowsPerPage={''}
              rowsPerPageOptions={[]}
              rowsPerPage={10}
              page={page}
              onPageChange={handleChangePage}
              labelDisplayedRows={({ to, count }) =>
                {t('reports.optimize_counter.footer.colaborators', {to: to, count: count})}
              }
            />
          </ChartWithNoteWrapper>
        </BodyContainer>
      </ContentContainer>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingData}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />

      <OrdinatorDrawer
        open={openOrderDrawer}
        onClose={() => {
          setOpenOrderDrawer(false)
        }}
        onOrderSave={onSelectOrderSave}
      />
    </Container>
  )
}

export default ReportOptimizeCounterMeasure
